import { AmpFlagSet, amplitudeClients } from './amplitude';
import {
    cookieStorageAvailable,
    disableGreetingSuppressionOnLogin,
    getBrand,
    getConversationIdFromToken,
    getPlatform,
    getTenantName,
    getUrlParam,
    isBlank,
    isBillingLiveChatEnabled,
    isMobile,
    isRehydratedMessage,
    isSyntheticTest,
    localStorageAvailable,
    useToastGreeting,
    generateAmplitudeUserId,
} from './helpers';
import { BrandParentUrl, InitLoaderValues, Session } from './types';
import { MYCHART } from './constants';
import {
    logEvent as logInsightsEvent,
    logDebugInsights,
    getCorrelationId,
} from './logging';
import { GraceFeatureFlags } from './featureFlags';

import { Tier } from './config';

export enum ANALYTICS_EVENT_TYPES {
    CATEGORICAL_AUTO_REDIRECT = 'MA Categorical - Auto Redirect',
    MED_CALL_PHARMACY = 'Medication - Call Pharmacy',
    USER_TYPED_SEND_MESSAGE = 'User Typed - Send Message',
}

const determineChannel = (identifier: string, referrer: string): string => {
    console.log(
        'determine channel - identifier: ' +
            identifier +
            ' , referrer: ' +
            referrer
    );
    switch (identifier) {
        case 'mobile':
            return 'Health Connect';
    }
    if (referrer.includes('lumedic')) {
        return 'Lumedic';
    }
    if (referrer.includes('virtual')) {
        return 'Express Care Virtual';
    }
    if (referrer.includes('urgent-care')) {
        return 'Same-day Care';
    }
    if (
        referrer.includes('prov') ||
        referrer.includes('swed') ||
        referrer.includes('file://')
    ) {
        return 'Coronavirus Advisory Pages';
    }
    if (referrer.includes('testWebsite.html')) {
        return 'Bot Testing Page';
    }
    console.error("Couldn't determine channel");
    console.error('identifier: ' + identifier + ', referrer: ' + referrer);
    return '';
};

export const getReferrerWithoutTrailingQuestionMark = (
    referrer: string
): string => {
    if (referrer.charAt(referrer.length - 1) === '?') {
        return referrer.substring(0, referrer.length - 1);
    }
    return referrer;
};

const extractHostname = (url: string): string => {
    if (!isBlank(url)) {
        let hostname;
        //find & remove protocol (http, ftp, etc.) and get hostname
        if (url.indexOf('//') > -1) {
            hostname = url.split('/')[2];
        } else {
            hostname = url.split('/')[0];
        }
        //find & remove port number
        hostname = hostname.split(':')[0];
        //find & remove "?"
        hostname = hostname.split('?')[0];
        return hostname;
    }
    return '';
};

export const shouldSendAnalyticsEvents = (
    featureFlags?: AmpFlagSet,
    currWatermark?: string
): boolean => {
    if (
        featureFlags &&
        currWatermark &&
        isRehydratedMessage(featureFlags, currWatermark)
    ) {
        logDebugInsights({
            name: 'ShouldSendAnalyticsEvents',
            properties: {
                value: false,
                reason: 'Rehydrated',
            },
        });
        return false;
    }
    if (isSyntheticTest()) {
        return false;
    }
    return true;
};

export const logEvent = (eventName: string, eventDetails?: object) => {
    if (shouldSendAnalyticsEvents() && amplitudeClients.isInitialized()) {
        if (
            eventName === 'setUserId' &&
            eventDetails &&
            'userId' in eventDetails
        ) {
            // This event comes from rasa-grace, where the amplitude user id is generated from that user's JWT
            // in the logged out case this will be the first time a user id is set for the user.
            // eslint-disable-next-line  @typescript-eslint/no-explicit-any
            amplitudeClients.setUserId((eventDetails as any).userId);
        } else {
            amplitudeClients.logEvent(eventName, eventDetails);
            parent.window.postMessage(
                { type: 'analytics-event', eventName, eventDetails },
                '*'
            );
        }
    }
};

export const sendExternalLinkSelected = (text: string, url: string) => {
    logInsightsEvent({
        name: 'LinkClick',
        properties: { text, url },
    });
    logEvent('External link selected', {
        link_text: text,
        link_url: url,
    });
};

// eslint-disable-next-line  @typescript-eslint/no-explicit-any
export const initializeAnalyticsEvents = (
    userProperties: { [key: string]: any },
    amplitudeAnalyticsKey: string,
    userId?: string,
    amplitudeReverseProxyEndpoint?: string
): void => {
    if (shouldSendAnalyticsEvents()) {
        const eventsClient = amplitudeClients.initializeAnalyticsClient(
            amplitudeAnalyticsKey,
            userId,
            amplitudeReverseProxyEndpoint
        );

        if (!eventsClient) return;

        // set userId and sessionId if they are provided
        const sessionId = getUrlParam('ampsid');
        if (!isBlank(sessionId)) {
            amplitudeClients.setSessionId(parseInt(sessionId));
        }
        const deviceId = amplitudeClients.getDeviceId();
        if (!isBlank(deviceId)) {
            amplitudeClients.setDeviceId(deviceId);
        }

        amplitudeClients.setUserProperties(userProperties, eventsClient);
    }
};

export const getBaseUserProperties = (
    initLoaderValues: InitLoaderValues | null,
    tier: Tier,
    brandParentUrl: BrandParentUrl
) => {
    const referrer = getUrlParam('referrer');
    const tenant = getTenantName();
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const userProperties: { [key: string]: any } = {
        bot_domain: window.document.domain,
        brand: getAnalyticsBrand(brandParentUrl),
        channel: determineChannel(isMobile() ? 'mobile' : '', referrer),
        chatbot_tenant: `grace-${tenant}`,
        chatbot_version: 'Grace 3.0',
        cookies_enabled: cookieStorageAvailable(tier),
        correlation_id: getCorrelationId(),
        disable_greeting_suppression_on_login:
            disableGreetingSuppressionOnLogin() || false,
        grace_fab_with_menu: !useToastGreeting(),
        grace_web_fab: true,
        grace_web_hide_callout: initLoaderValues?.disableCallout || false,
        local_storage_enabled: localStorageAvailable(),
        platform: getPlatform(),
        rasa_bot: true,
        redesign: true,
        referrer: getReferrerWithoutTrailingQuestionMark(referrer),
        referring_domain: extractHostname(referrer),
        revc_livechat: isBillingLiveChatEnabled() || false,
        site_referral: referrer.split('?')[0],
        tier,
        utmCampaign: getUrlParam('utm_campaign'),
        utmMedium: getUrlParam('utm_medium'),
        utmSource: getUrlParam('utm_source'),
        viewport: `${window.screen.width} x ${window.screen.height}`,
    };

    if (initLoaderValues?.hideMenuDurationMinutes) {
        userProperties.suppress_menu_duration =
            initLoaderValues.hideMenuDurationMinutes;
    }

    return userProperties;
};

export const setFeatureFlagAmplitudeUserProperties = (
    featureFlags: AmpFlagSet,
    logged_in: boolean
): void => {
    const userProperties: any = {
        graceclosed_redirect: true,
        logged_in,
        'message-assist-greeting-optimization-manual':
            featureFlags[GraceFeatureFlags.MessageAssistGreetingOptimization] ||
            false,
        rehydration:
            featureFlags[GraceFeatureFlags.DirectLineReconnect] || false,
    };
    if (getTenantName() === MYCHART && !logged_in) {
        userProperties.remove_location =
            featureFlags[GraceFeatureFlags.OmitAskUserLocationOnStart] || false;
    }
    amplitudeClients.setUserProperties(userProperties);
};

export const setSessionAmplitudeUserProperties = (session: Session): void => {
    const userProperties = {
        bot_userid: session.userId,
        conversation_id:
            getConversationIdFromToken(session.connectorToken) || 'unknown',
    };
    amplitudeClients.setUserProperties(userProperties);
};

export const setAnalyticsUserProperties = (
    featureFlags: AmpFlagSet,
    activity: {
        name: string;
        value?: any;
    },
    currWatermark?: string
): void => {
    if (
        shouldSendAnalyticsEvents(featureFlags, currWatermark) &&
        amplitudeClients.isInitialized()
    ) {
        switch (activity.name) {
            case 'amplitudeEvent':
                if (
                    activity.value.eventProperties &&
                    activity.value.eventProperties.vc
                ) {
                    amplitudeClients.setUserProperties({
                        vc: activity.value.eventProperties.vc,
                    });
                }
                break;
        }
    }
};

export const analyticsLinkClickHandler = (e: any) => {
    const target = e.target as HTMLAnchorElement;
    let url = target.href;
    if (url.indexOf('?') > 0) {
        url = url.substring(0, url.indexOf('?'));
    }

    let currentTarget: HTMLElement | null = target;
    if (!currentTarget?.dataset?.kbIntent) {
        while (currentTarget && currentTarget.dataset?.botMsg !== 'true') {
            currentTarget = currentTarget.parentElement;
        }
    }
    const kbIntent = currentTarget?.dataset?.kbIntent;
    const textContent = target.textContent;
    logInsightsEvent({
        name: 'LinkClick',
        properties: { text: textContent || '', url: url || '' },
    });
    logEvent('External link selected', {
        link_url: url || '',
        link_text: textContent || '',
        kb_intent: kbIntent,
    });
};

export const getAnalyticsBrand = (brandParentUrl: BrandParentUrl) => {
    const brand = getBrand(brandParentUrl);
    return brand || 'Not Set';
};

export const flushEventsAsync = async () => {
    if (shouldSendAnalyticsEvents() && amplitudeClients.isInitialized()) {
        await amplitudeClients.flushEventsAsync();
    }
};

export const getAnalyticsUserId = (
    initLoaderValues: InitLoaderValues | undefined | null,
    healthSystemToken: string | undefined | null
): string | undefined => {
    if (initLoaderValues && initLoaderValues.amplitudeUserId) {
        return initLoaderValues.amplitudeUserId;
    }

    // Generate amplitude userId for logged in users
    if (healthSystemToken) {
        return generateAmplitudeUserId(healthSystemToken) || undefined;
    }
};
